import { dynamicImportWithRetry } from "@fatso83/retry-dynamic-import";
import { i18n } from "@lingui/core";
import { detect, fromNavigator } from "@lingui/detect-locale";
import * as Sentry from "@sentry/react";

import Config from "@app/config";
import logger from "@app/utils/logger";

import { messages as enMessages } from "./en/messages";

/**
 * Constants
 */

export const LOCALES: Record<string, string> = {
  en: "English",
};

export const DEFAULT_LOCALE = "en";

export const LOCALE_FALLBACKS: Record<string, string> = {};

export const USER_REQUESTED_LOCALE =
  detect(fromNavigator(), DEFAULT_LOCALE) || DEFAULT_LOCALE;

/**
 * Feature flagged locales
 *
 * How to add a new locale behind a feature flag:
 *
 * 1. Create a feature flag in our config per normal
 * 2. Add a condition using that feature flag below
 * 3. Conditionally add the new locale string to `LOCALES`
 * 4. Conditionally add desired country fallback codes mapped to the new locale string to `LOCALE_FALLBACKS`
 *
 * Example:
 *
 * if (Config.flags.enableSpanishLocale) {
 *   LOCALES["es"] = "Español";
 *   LOCALE_FALLBACKS["es-MX"] = "es";
 * }
 *
 * 5. When you are ready to release the new language, enable the feature flag in each environment per normal
 *
 * When the feature flag is no longer needed:
 *
 * 1. Remove feature flag per normal
 * 2. Remove conditional code below
 * 3. Hardcode the new locale string in `LOCALES` above
 * 4. Hardcode desired country fallback codes in `LOCALE_FALLBACKS` above
 */

if (Config.flags.enableSpanishLocale) {
  LOCALES["es"] = "Español";

  // https://localizely.com/language-code/es/
  const spanishSpeakingCountryCodes = [
    "es-419",
    "es-AR",
    "es-BO",
    "es-BR",
    "es-BZ",
    "es-CL",
    "es-CO",
    "es-CR",
    "es-CU",
    "es-DO",
    "es-EC",
    "es-ES",
    "es-GQ",
    "es-GT",
    "es-HN",
    "es-MX",
    "es-NI",
    "es-PA",
    "es-PE",
    "es-PH",
    "es-PR",
    "es-PY",
    "es-SV",
    "es-US",
    "es-UY",
    "es-VE",
  ];

  spanishSpeakingCountryCodes.forEach((countryCode) => {
    LOCALE_FALLBACKS[countryCode] = "es";
  });
}

/**
 * Locale switcher functions
 */

/** Get the correct locale, a fallback or the default */
const getLocaleOrFallback = (locale: string) => {
  if (locale in LOCALES) {
    return locale;
  }

  if (locale in LOCALE_FALLBACKS) {
    return LOCALE_FALLBACKS[locale];
  }

  return DEFAULT_LOCALE;
};

/** Switch the current locale (dynamically imports messages catalog) */
export const switchLocale = async (locale: string) => {
  const localeToLoad = getLocaleOrFallback(locale);

  logger.info(
    `Loading ${localeToLoad} locale messages for user requested locale: ${locale}`
  );

  try {
    const { messages } = await dynamicImportWithRetry(
      () => import(`./${localeToLoad}/messages.ts`)
    );
    i18n.loadAndActivate({ locale: localeToLoad, messages });
  } catch (e) {
    Sentry.captureException(e);
  }
};

// Default to English
i18n.loadAndActivate({ locale: DEFAULT_LOCALE, messages: enMessages });

export default i18n;
